import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Row, Col, Form, FormGroup, FormLabel, Card, InputGroup, Pagination, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SearchIcon from "../../../images/search-icon.svg";
import Icon1 from "../../../images/support/icon-all-tickets.png";
import Icon2 from "../../../images/support/icon-open-tickets.png";
import Icon3 from "../../../images/support/icon-progress.png";
import Icon4 from "../../../images/support/icon-check.png";
import { useNavigate } from 'react-router-dom';
import ShortMain from "../../../images/short-icon-main.svg";
import ShortUp from "../../../images/short-icon-top.svg";
import ShortDown from "../../../images/short-icon-bottom.svg";
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { supportApi, GetTicketsParams, Ticket as ApiTicket } from './SupportApi.tsx';
import { HiOutlineDownload } from 'react-icons/hi';
import DateRangeSelector from "../../DateRangeSelector";
import ExportModal from "../../ExportModal";
import { exportSupportTickets, downloadFile, getExportFilename } from '../../../utils/exportUtils';
import "./Support.scss";

type Ticket = ApiTicket;

interface TicketCounts {
  total: number;
  open: number;
  inProgress: number;
  completed: number;
}

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

interface DateRange {
  startDate: string;
  endDate: string;
}


const Support = () => {
  const { withLoader } = useApiLoader();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });
  const [ticketCounts, setTicketCounts] = useState<TicketCounts>({
    total: 0,
    open: 0,
    inProgress: 0,
    completed: 0
  });

  const [selectedCardFilter, setSelectedCardFilter] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useState(10);


  const formatDate = (dateString: any) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return '';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  };

  // Format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  const fetchTicketCounts = async () => {
    try {
      setStatsLoading(true);

      const response = await withLoader(
        () => supportApi.getTicketCounts(),
        'Loading ticket statistics...'
      );

      if (response.status === 'Success' && response.ticketCounts) {
        setTicketCounts(response.ticketCounts);
      }
    } catch (error: any) {
      // Error fetching ticket counts
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchTickets = useCallback(async (
    page = 1,
    search = '',
    status = '',
    limit = itemsPerPage,
    sortBy = '',
    sortOrder = '',
    cardFilter = '',
    startDate = '',
    endDate = ''
  ) => {
    try {
      setLoading(true);

      const params: GetTicketsParams = {
        page,
        limit,
        search: search.trim()
      };

      // Use either status filter or card filter (card filter takes precedence)
      if (cardFilter && cardFilter !== '') {
        params.status = cardFilter;
      } else if (status && status !== '') {
        params.status = status;
      }

      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder as 'asc' | 'desc';
      }

      // Add date range parameters
      if (startDate) {
        params.startDate = formatDateForAPI(startDate);
      }
      if (endDate) {
        params.endDate = formatDateForAPI(endDate);
      }

      const response = await withLoader(
        () => supportApi.getAllTickets(params),
        'Loading tickets...'
      );

      if (response.status === 'Success' && response.data) {
        const ticketData = response.data;
        const paginationData = response.pagination || {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        };

        setTickets(ticketData);
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);

      } else {
        setTickets([]);
        setPagination({
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        });
      }
    } catch (error: any) {
      setTickets([]);
      setPagination({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1
      });
    } finally {
      setLoading(false);
    }
  }, [withLoader]);


  const handleCardClick = (cardType: string, count: number) => {
    // Don't allow clicking if count is 0
    if (count === 0) return;

    let statusFilter = '';

    switch (cardType) {
      case 'open':
        statusFilter = 'open';
        break;
      case 'inProgress':
        statusFilter = 'in-progress';
        break;
      case 'completed':
        statusFilter = 'completed';
        break;
      case 'all':
      default:
        statusFilter = '';
        break;
    }

    setSelectedCardFilter(statusFilter);
    setStatusFilter(''); // Clear the dropdown filter
    setCurrentPage(1);

    fetchTickets(1, searchTerm, '', itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, statusFilter, dateRange.startDate, dateRange.endDate);
  };

  const handleSort = (field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        setSortConfig({ sortBy: '', sortOrder: '' });
        fetchTickets(currentPage, searchTerm, statusFilter, itemsPerPage, '', '', selectedCardFilter, dateRange.startDate, dateRange.endDate);
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    fetchTickets(currentPage, searchTerm, statusFilter, itemsPerPage, field, newSortOrder, selectedCardFilter, dateRange.startDate, dateRange.endDate);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>
      );
    }

    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page);
      fetchTickets(page, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, dateRange.startDate, dateRange.endDate);
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSelectedCardFilter(value);
    setCurrentPage(1);

    fetchTickets(
      1,
      searchTerm,
      value,
      itemsPerPage,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      value,
      dateRange.startDate,
      dateRange.endDate
    );
  };

  // Handle date range change
  const handleDateRangeChange = useCallback((dateRangeString: string, isCustom?: boolean) => {
    // Parse the date range string "MM/DD/YYYY - MM/DD/YYYY"
    if (!dateRangeString || dateRangeString.trim() === '') {
      // Clear the filter
      setDateRange({ startDate: '', endDate: '' });
      setCurrentPage(1);
      fetchTickets(
        1,
        searchTerm,
        statusFilter,
        itemsPerPage,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        selectedCardFilter,
        '',
        ''
      );
      return;
    }

    // Split the date range string
    const dates = dateRangeString.split(' - ');
    if (dates.length === 2) {
      const startDateStr = dates[0].trim();
      const endDateStr = dates[1].trim();

      // Convert MM/DD/YYYY to YYYY-MM-DD without timezone conversion
      const parseDate = (dateStr: string): string => {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const formattedStartDate = parseDate(startDateStr);
      const formattedEndDate = parseDate(endDateStr);

      // Validate dates
      if (formattedStartDate > formattedEndDate) {
        toast.error('Start date cannot be after end date');
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
      setCurrentPage(1);

      fetchTickets(
        1,
        searchTerm,
        statusFilter,
        itemsPerPage,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        selectedCardFilter,
        formattedStartDate,
        formattedEndDate
      );
    }
  }, [searchTerm, statusFilter, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, fetchTickets]);

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');
      const blob = await exportSupportTickets('excel', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Support_Tickets', 'excel', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error: any) {
      console.error('Export Excel error:', error);
      if (error.response?.status === 404) {
        toast.error('No support tickets found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export Excel file. Please try again.');
      }
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Preparing PDF export...');
      const blob = await exportSupportTickets('pdf', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Support_Tickets', 'pdf', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('PDF file downloaded successfully!');
    } catch (error: any) {
      console.error('Export PDF error:', error);
      if (error.response?.status === 404) {
        toast.error('No support tickets found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export PDF file. Please try again.');
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchTickets(1, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, dateRange.startDate, dateRange.endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchTickets(1, '', '', itemsPerPage, '', '', '', '', '');
    fetchTicketCounts();
  }, []);

  // Re-fetch when items per page changes
  useEffect(() => {
    setCurrentPage(1);
    fetchTickets(1, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, dateRange.startDate, dateRange.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    let displayStatus = status;

    switch (status?.toLowerCase()) {
      case 'open':
        badgeClass = 'or-badge';
        displayStatus = 'Open';
        break;
      case 'in-progress':
        badgeClass = 'blu-badge';
        displayStatus = 'In Progress';
        break;
      case 'completed':
        badgeClass = 'gre-badge';
        displayStatus = 'Completed';
        break;
      default:
        badgeClass = 'secondary';
    }
    return <Badge bg={badgeClass}>{displayStatus}</Badge>;
  };

  const data = [
    {
      icon: Icon1,
      value: ticketCounts.total,
      label: "All Tickets",
      colorClass: "blue-text",
      onClick: () => handleCardClick('all', ticketCounts.total),
      isActive: selectedCardFilter === '',
      isClickable: ticketCounts.total > 0
    },
    {
      icon: Icon2,
      value: ticketCounts.open,
      label: "Open",
      colorClass: "orange-text",
      onClick: () => handleCardClick('open', ticketCounts.open),
      isActive: selectedCardFilter === 'open',
      isClickable: ticketCounts.open > 0
    },
    {
      icon: Icon3,
      value: ticketCounts.inProgress,
      label: "In Progress",
      colorClass: "blue-text",
      onClick: () => handleCardClick('inProgress', ticketCounts.inProgress),
      isActive: selectedCardFilter === 'in-progress',
      isClickable: ticketCounts.inProgress > 0
    },
    {
      icon: Icon4,
      value: ticketCounts.completed,
      label: "Completed",
      colorClass: "green-text",
      onClick: () => handleCardClick('completed', ticketCounts.completed),
      isActive: selectedCardFilter === 'completed',
      isClickable: ticketCounts.completed > 0
    },
  ];

  return (
    <>
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
          <div className="icon-grid-sec">
            <ul className='icon-grid mb-4'>
              <div className='row-4 m-0'>
                {data.map((item, idx) => (
                  <li
                    className={`icon-block ${item.isActive ? 'active-card' : ''} ${!item.isClickable ? 'not-clickable' : ''}`}
                    key={idx}
                    onClick={item.isClickable ? item.onClick : undefined}
                    style={{ cursor: item.isClickable ? 'pointer' : 'not-allowed' }}
                  >
                    <div className="icon-block-in d-flex justify-content-between align-items-center">
                      <figure><img src={item.icon} alt="" /></figure>
                      <div className="icon-grid-cont">
                        <h2 className={item.colorClass}>
                          {statsLoading ? '...' : item.value}
                        </h2>
                        <p>{item.label}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </div>
              <div className="clearfix"></div>
            </ul>
          </div>

          <div className="management-sec">
            <div className="management-sec-in">
              <div className='main-table'>
                <div className="tab-content tab-body">
                  <div className="referral-discount mb-3">
                    {/* Filter and Search Row */}
                    <div className="mb-3 main-table-filter">
                      <div className="row g-2 align-items-stretch">
                        {/* Search Field */}
                        <div className='col-lg-3 col-md-6 col-12'>
                          <div className='main-table-search h-100'>
                            <FormGroup className='form-group search-box mb-0 h-100'>
                              <InputGroup className="h-100">
                                <Form.Control
                                  type="text"
                                  placeholder="Search support tickets..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="h-100"
                                />
                                <InputGroup.Text>
                                  <img src={SearchIcon} alt='' />
                                </InputGroup.Text>
                              </InputGroup>
                            </FormGroup>
                          </div>
                        </div>

                        {/* Filter Dropdown */}
                        <div className='col-lg-2 col-md-6 col-12'>
                          <div className='select-field h-100'>
                            <FormGroup className='form-group mb-0 h-100'>
                              <Form.Select
                                value={statusFilter}
                                onChange={(e) => handleStatusFilterChange(e.target.value)}
                                className="h-100"
                              >
                                <option value="">Filter by Status</option>
                                <option value="open">Open</option>
                                <option value="in-progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </Form.Select>
                            </FormGroup>
                          </div>
                        </div>

                        {/* Spacer to push date range and export to the right */}
                        <div className='col-lg-3 d-none d-lg-block'></div>

                        {/* Date Range Picker */}
                        <div className='col-lg-3 col-md-9 col-12'>
                          <div className='calender-block h-100'>
                            <DateRangeSelector
                              onDateRangeChange={handleDateRangeChange}
                            />
                          </div>
                        </div>

                        {/* Export Button */}
                        <div className='col-lg-1 col-md-3 col-12'>
                          <Button
                            variant="primary"
                            onClick={handleExport}
                            className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                          >
                            <HiOutlineDownload size={18} />
                            <span>Export</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive mt-3">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Ticket ID</th>
                    <th>User Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Posted on</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">Loading tickets...</span>
                      </td>
                    </tr>
                  ) : tickets.length > 0 ? (
                    tickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        onClick={() => navigate(`/support/ticket/${ticket.id}`)}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <td>{ticket.ticketId}</td>
                        <td>{ticket.userName || 'N/A'}</td>
                        <td>{ticket.userEmail || 'N/A'}</td>
                        <td>{ticket.subject}</td>
                        <td>{formatDate(ticket.supportTicketRaisedDate)}</td>
                        <td className="info-badge-area">
                          <p className="d-flex align-items-center gap-1 mb-0">
                            {getStatusBadge(ticket.status)}
                          </p>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <p className="text-muted mb-0">
                          {searchTerm
                            ? 'No tickets found matching your search criteria.'
                            : 'No tickets found.'
                          }
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {!loading && tickets.length > 0 && pagination.totalPages > 1 && (
              <div className="pagination-wrapper mt-3">
                <div className="pagination-info-row">
                  <div className="text-muted">
                    Showing {((pagination.currentPage - 1) * itemsPerPage) + 1} to {Math.min(pagination.currentPage * itemsPerPage, pagination.totalCount)} of {pagination.totalCount} entries
                    {(dateRange.startDate || dateRange.endDate) && (
                      <span className="ms-2">(filtered by date range)</span>
                    )}
                  </div>
                </div>
                <div className="pagination-controls-row d-flex align-items-center gap-3">
                  <div className="pagination-per-page d-flex align-items-center gap-2">
                    <span className="pagination-label">Show</span>
                    <select
                      className="form-select form-select-sm pagination-select"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      disabled={pagination.currentPage === 1 || loading}
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                    />

                    {pagination.currentPage > 3 && (
                      <>
                        <Pagination.Item
                          onClick={() => handlePageChange(1)}
                          disabled={loading}
                        >
                          1
                        </Pagination.Item>
                        {pagination.currentPage > 4 && (
                          <Pagination.Ellipsis disabled />
                        )}
                      </>
                    )}

                    {Array.from({ length: pagination.totalPages }, (_, index) => index + 1)
                      .filter(page =>
                        page >= Math.max(1, pagination.currentPage - 2) &&
                        page <= Math.min(pagination.totalPages, pagination.currentPage + 2)
                      )
                      .map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === pagination.currentPage}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </Pagination.Item>
                      ))
                    }

                    {pagination.currentPage < pagination.totalPages - 2 && (
                      <>
                        {pagination.currentPage < pagination.totalPages - 3 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={loading}
                        >
                          {pagination.totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    <Pagination.Next
                      disabled={pagination.currentPage === pagination.totalPages || loading}
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </div>
            )}
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Container>
      </section>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        title="Export Support Tickets Data"
      />
    </>
  );
};

export default Support;